// src/context/DataContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { useStaticQuery, graphql } from "gatsby";

// 1. Create the context
const DataContext = createContext();

const safeString = (val) =>
  typeof val === "string" ? val.trim().toLowerCase() : "";

const normalizeHazardId = (val) =>
  safeString(val).replace(/_/g, "-").replace(/\s+/g, "-");

// 2. Provider component
export const DataProvider = ({ children, shouldFetchData }) => {
  const [data, setData] = useState(null);

  // 3. Static GraphQL query: group each data source clearly
  const queryResult = useStaticQuery(graphql`
    query {
      allChecklistItemsCsv {
        nodes {
          checklist_item_id
          title
          description
          operational_area_default
          applies_to_all_businesses
          default_phase
          source_name
          source_link
          difficulty_level
        }
      }
      allMappingsCsv {
        nodes {
          mapping_id
          checklist_item_id
          hazard_id
          alert_type
          operational_area_override
          routine_frequency
          industry_scope_type
          industry_codes
          business_size_scope
          display_group
        }
      }
      allHazardsAlertsCsv {
        nodes {
          hazard_id
          hazard_category
          hazard_name
          trigger_type
          trigger_code
          trigger_severity_rank
          description
          active_flag
        }
      }

      allAlertDataCsv {
        nodes {
          EXCLUDE
          formatted_name
          geography_type
          jurisdiction
          website
          Notes
        }
      }
      allDisasterAppsCsv {
        nodes {
          app_name
          apple_app_store_link
          google_play_link
          website
          type
        }
      }
      allRadioNwsStationsCsv {
        nodes {
          County
          Site_Name
          Callsign
          Frequency
          Area
          Notes
        }
      }
      allRadioEasFmStationsCsv {
        nodes {
          COUNTY
          EAS_AREA
          LP_1
          LP_2
        }
      }
      allGasProviderJurisdictionsCsv {
        nodes {
          entity_name
          jurisdiction
        }
      }
      allCompiledDataCsv {
        nodes {
          EXCLUDE
          TOP
          callsign
          parent_group
          entity_name
          formatted_name
          website
          rss
          twitter
          facebook
          instagram
          donate_link
          alert_signup
          outages_map
          live_stream_link
          live_stream_embed
          apple_news_app
          apple_weather_app
          google_weather_app
          main_phone
          alternate_phone
          outage_phone_one
          outage_phone_2
          street_address
          street
          street2
          city
          state
          zip
          lat
          long
          jurisdiction
          type
          geography_type
        }
      }
      allCitiesAndCountiesCsv {
        nodes {
          County
          City
        }
      }
      allWaterProviderGeographiesCsv {
        nodes {
          entity_name
          jurisdiction
        }
      }
      allElectricProviderJurisdictionsCsv {
        nodes {
          entity_name
          jurisdiction
        }
      }
      allNwsCountyCodesCsv {
        nodes {
          nws_county_code
          county
          center_lat
          center_long
          fips
        }
      }
      allNwsRegionsCountyMatchCsv {
        nodes {
          CWA
          COUNTY
        }
      }
      allTxdotDistrictGeographiesCsv {
        nodes {
          district
          jurisdiction
        }
      }
      allRegionalVoadsByCountyCsv {
        nodes {
          county
          entity_name
        }
      }
      allNewsStationCountiesCsv {
        nodes {
          EXCLUDE
          Network
          StationID
          COUNTY
          Signal_Quality
        }
      }
      allSchoolDistrictCountiesCsv {
        nodes {
          school_district
          jurisdiction
        }
      }
      allLongTermRecoveryGroupJurisdictionsCsv {
        nodes {
          entity_name
          jurisdiction
        }
      }

      allHazardDescriptionsCsv {
        nodes {
          Hazard
          Description
        }
      }
      allHazardTimingCsv {
        nodes {
          Hazard
          AlertType
          Timeframe
          ChipTimeframe
        }
      }
      allNriTableCountiesTexasCsv {
        nodes {
          COUNTY
          STATE
          COUNTYFIPS
          POPULATION
          AVLN_EALT
          AVLN_EALP
          CFLD_EALT
          CFLD_EALP
          CWAV_EALT
          CWAV_EALP
          DRGT_EALA
          ERQK_EALT
          ERQK_EALP
          HAIL_EALT
          HAIL_EALP
          HWAV_EALT
          HWAV_EALP
          HRCN_EALT
          HRCN_EALP
          ISTM_EALT
          ISTM_EALP
          LNDS_EALT
          LNDS_EALP
          LTNG_EALT
          LTNG_EALP
          RFLD_EALT
          RFLD_EALP
          SWND_EALT
          SWND_EALP
          TRND_EALT
          TRND_EALP
          TSUN_EALT
          TSUN_EALP
          VLCN_EALT
          VLCN_EALP
          WFIR_EALT
          WFIR_EALP
          WNTW_EALT
          WNTW_EALP
        }
      }

      allBeforeTipsAndToolsCsv {
        nodes {
          type
          hazard
          title
          description
          source_name
          source_link
        }
      }
      allDisastersCsv {
        nodes {
          DisasterID
          Name
          Type
          Status
          StartDate
          EndDate
          Description
          Visible
        }
      }

      allDisasterJurisdictionsCsv {
        nodes {
          DeclarationID
          JurisdictionID
          JurisdictionName
        }
      }

      allDeclarationsCsv {
        nodes {
          DeclarationID
          DisasterID
          Name
          Type
          DeclarationDate
          EndDate
          Description
          StatewideFlag
          FemaEmergencyDeclaration
          FemaMajorDeclaration
          SbaDeclaration
          UsdaDeclaration
        }
      }

      allResourcesCsv {
        nodes {
          ResourceID
          DisasterID
          JurisdictionID
          Category
          SourceType
          Title
          Organization
          Description
          ResourceNote
          URL
          Phone
          AlternativePhone
          Email
          Featured
          Active
          Address
          City
          State
          Zip
          Latitude
          Longitude
          OpenDate
          CloseDate
        }
      }

      allExternalSourcesCsv {
        nodes {
          SourceID
          DisasterID
          Category
          SourceType
          SourceName
          SourceURL
          Active
        }
      }

      allCategoriesCsv {
        nodes {
          CategoryID
          Label
          DisplayOrder
          Icon
          ExternalCapable
        }
      }
    }
  `);

  // 4. Handle builds, transform, and filter where needed
  useEffect(() => {
    if (shouldFetchData && queryResult) {
      const filteredData = queryResult.allCompiledDataCsv.nodes.filter(
        (node) => {
          const areAllFieldsBlank = [
            node.website,
            node.rss,
            node.twitter,
            node.facebook,
            node.instagram,
            node.donate_link,
            node.alert_signup,
            node.outages_map,
            node.live_stream_link,
            node.live_stream_embed,
            node.apple_news_app,
            node.apple_weather_app,
            node.google_weather_app,
            node.main_phone,
            node.alternate_phone,
            node.outage_phone_one,
            node.outage_phone_2,
            node.street_address,
            node.street,
            node.street2,
            node.city,
            node.state,
            node.zip,
            node.lat,
            node.long,
          ].every((field) => !field);

          const isNotHealthAndHumanServices =
            node.entity_name !== "Health and Human Services" &&
            node.formatted_name !== "Health and Human Services";
          const isNotCentralTexasVoad =
            node.parent_group !== "Central Texas VOAD";

          return (
            !areAllFieldsBlank &&
            isNotHealthAndHumanServices &&
            isNotCentralTexasVoad
          );
        },
      );

      // NEW: build joined checklist data from items + mappings
      const items = queryResult.allChecklistItemsCsv?.nodes || [];
      const mappings = queryResult.allMappingsCsv?.nodes || [];

      const itemById = new Map(items.map((i) => [i.checklist_item_id, i]));

      const joinedChecklist = mappings
        .filter((m) => m.industry_scope_type === "all_industries")
        .map((m) => {
          const item = itemById.get(m.checklist_item_id) || {};
          return {
            Hazard: m.hazard_id,
            Phase: item.default_phase || "preparedness",
            AlertType: m.alert_type,
            Category: m.display_group || item.operational_area_default,
            Action: item.description,
            Title: item.title,
            ChecklistItemId: m.checklist_item_id,
            source: item.source_name || "",
            sourceLink: item.source_link || "",
            difficulty: item.difficulty_level || "essential",
            // IMPORTANT: carry routine_frequency through so evacuation can bucket Ready/Set/Go
            routine_frequency: m.routine_frequency,
          };
        });

      // ---------- NEW: supply list join ----------
      const supplyMappings = mappings.filter(
        (m) =>
          typeof m.checklist_item_id === "string" &&
          m.checklist_item_id.startsWith("SI"),
      );

      const joinedSupplyLists = supplyMappings.map((m) => {
        const item = itemById.get(m.checklist_item_id) || {};
        const operationalArea =
          m.operational_area_override ||
          item.operational_area_default ||
          "General";

        return {
          Hazard: normalizeHazardId(m.hazard_id),
          OperationalArea: operationalArea,
          ChecklistItemId: m.checklist_item_id,
          Title: item.title,
          Description: item.description,
          source: item.source_name || "",
          sourceLink: item.source_link || "",
          difficulty: item.difficulty_level || "essential",
        };
      });

      // CRITICAL LOGS for debugging data stream
      console.log("[DataContext] items count:", items.length);
      console.log("[DataContext] mappings count:", mappings.length);
      console.log("[DataContext] supplyMappings count:", supplyMappings.length);
      console.log(
        "[DataContext] joinedSupplyLists sample:",
        joinedSupplyLists.slice(0, 5),
      );

      // NEW: normalize BeforeTipsAndTools hazards into an array
      const beforeTipsRaw = queryResult.allBeforeTipsAndToolsCsv?.nodes || [];
      const beforeTipsLinks = beforeTipsRaw.map((row) => {
        const hazardIds = (row.hazard || "")
          .split(",")
          .map((part) => normalizeHazardId(part))
          .filter(Boolean);
        return {
          ...row,
          hazardIds, // e.g. ["wildfire", "winter-weather"]
        };
      });
      // ----- Disaster Data -----

      const disasters = queryResult.allDisastersCsv?.nodes || [];

      const disasterJurisdictions =
        queryResult.allDisasterJurisdictionsCsv?.nodes || [];

      const declarations = queryResult.allDeclarationsCsv?.nodes || [];

const resources = queryResult.allResourcesCsv?.nodes || [];

console.log(
  "RESOURCE ACTIVE CHECK",
  resources.map(r => ({
    id: r.ResourceID,
    title: r.Title,
    active: r.Active,
  }))
);
const isResourceClosed = (resource) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (resource.OpenDate) {
    const openDate = new Date(resource.OpenDate);
    openDate.setHours(0, 0, 0, 0);

    if (!Number.isNaN(openDate.getTime()) && today < openDate) {
      return true;
    }
  }

  if (resource.CloseDate) {
    const closeDate = new Date(resource.CloseDate);
    closeDate.setHours(0, 0, 0, 0);

    if (!Number.isNaN(closeDate.getTime()) && today > closeDate) {
      return true;
    }
  }

  return false;
};

      const processedResources = resources.map((resource) => ({
        ...resource,
        isClosed: isResourceClosed(resource),
      }));

      const externalSources = queryResult.allExternalSourcesCsv?.nodes || [];

      const resourceCategories = queryResult.allCategoriesCsv?.nodes || [];

      const activeDisasters = disasters.filter(
        (d) =>
          d.Status?.toLowerCase() === "active" &&
          String(d.Visible).toLowerCase() === "true",
      );

      // ----- Selectors / Helpers -----

      const getDisasterById = (disasterId) =>
        disasters.find((d) => d.DisasterID === disasterId);

      const getDeclarationByDisasterId = (disasterId) =>
        declarations.find((d) => d.DisasterID === disasterId);

      const getDisasterJurisdictions = (disasterId) => {
        const declarationIds = declarations
          .filter((d) => d.DisasterID === disasterId)
          .map((d) => String(d.DeclarationID));

        return disasterJurisdictions.filter((j) =>
          declarationIds.includes(String(j.DeclarationID)),
        );
      };
      const countyHasDisaster = (county, disasterId) => {
        const jurisdictions = getDisasterJurisdictions(disasterId);

        return jurisdictions.some((j) => j.JurisdictionName === county);
      };
      const getResourcesForDisaster = (disasterId, jurisdictionId = null) =>
        processedResources.filter((r) => {
          const disasterMatch = r.DisasterID === disasterId;

          const jurisdictionMatch =
            !jurisdictionId ||
            r.JurisdictionID === jurisdictionId ||
            r.JurisdictionID === "ALL";

          const active = String(r.Active).toLowerCase() === "true";

          return disasterMatch && jurisdictionMatch && active;
        });
      const hasLocalShelters = (jurisdictionId = null) => {
        return resources.some((r) => {
          const shelterCategory = r.Category === "Shelters";

          const jurisdictionMatch =
            !jurisdictionId ||
            r.JurisdictionID === jurisdictionId ||
            r.JurisdictionID === "ALL";

          const active = String(r.Active).toLowerCase() === "true";

          return shelterCategory && jurisdictionMatch && active;
        });
      };
      const getVisibleCategories = (disasterId, jurisdictionId = null) => {
        const categoryIds = new Set(["211", "Shelters"]);
        if (hasLocalShelters(jurisdictionId)) {
          categoryIds.add("Shelters");
        }
        console.log("=== getVisibleCategories ===");
        console.log("disasterId:", disasterId);
        console.log("jurisdictionId:", jurisdictionId);
        resources.forEach((resource) => {
          const disasterMatch = resource.DisasterID === disasterId;

          const jurisdictionMatch =
            !jurisdictionId ||
            resource.JurisdictionID === jurisdictionId ||
            resource.JurisdictionID === "ALL";

          const active = String(resource.Active).toLowerCase() === "true";
          console.log({
            resourceId: resource.ResourceID,
            category: resource.Category,
            resourceDisaster: resource.DisasterID,
            resourceJurisdiction: resource.JurisdictionID,
            disasterMatch,
            jurisdictionMatch,
            active,
          });
          if (disasterMatch && jurisdictionMatch && active) {
            categoryIds.add(resource.Category);
          }
        });

        externalSources.forEach((source) => {
          const active = String(source.Active).toLowerCase() === "true";

          if (source.DisasterID === disasterId && active) {
            categoryIds.add(source.Category);
          }
        });
        console.log(
          "CATEGORY IDS FOUND:",
          disasterId,
          jurisdictionId,
          Array.from(categoryIds),
        );
        const categories = resourceCategories.filter((category) =>
          categoryIds.has(category.CategoryID),
        );
        const hasResourceCenters =
          categoryIds.has("DRC") || categoryIds.has("Shelters");

        const transformedCategories = categories.filter(
          (c) => c.CategoryID !== "DRC" && c.CategoryID !== "Shelters",
        );

        if (hasResourceCenters) {
          transformedCategories.push({
            CategoryID: "ResourceCenters",
            Label: "Resource Centers",
            DisplayOrder: 3,
            Icon: "location",
            ExternalCapable: "TRUE",
          });
        }

        return transformedCategories.sort(
          (a, b) =>
            Number(a.DisplayOrder || 999) - Number(b.DisplayOrder || 999),
        );
        return resourceCategories
          .filter((category) => categoryIds.has(category.CategoryID))
          .sort(
            (a, b) =>
              Number(a.DisplayOrder || 999) - Number(b.DisplayOrder || 999),
          );
      };
      setData({
        ...queryResult,
        allCompiledDataCsv: { nodes: filteredData },
        nriHazards: queryResult.allNriTableCountiesTexasCsv.nodes,
        allChecklistData: { nodes: joinedChecklist },
        beforeTipsLinks,
        // NEW: expose supply lists
        allSupplyLists: { nodes: joinedSupplyLists },
        disasters,
        disasterJurisdictions,
        declarations,
        resources: processedResources,
        externalSources,
        resourceCategories,
        activeDisasters,

        getDisasterById,
        getDisasterJurisdictions,
        getResourcesForDisaster,
        getVisibleCategories,
        getDisasterJurisdictions,
        countyHasDisaster,
        getDeclarationByDisasterId,
        hasLocalShelters,
      });
    }
  }, [shouldFetchData, queryResult]);

  return <DataContext.Provider value={data}>{children}</DataContext.Provider>;
};

export const useData = () => useContext(DataContext);
